import React, { useState } from 'react';

const ComplianceDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview'|'states'|'audit'>('overview');
  const states = [
    {state:'GA',status:'Compliant',lastCheck:'2026-04-13',violations:0,certs:47},
    {state:'FL',status:'Review',lastCheck:'2026-04-13',violations:1,certs:31},
    {state:'TX',status:'Compliant',lastCheck:'2026-04-13',violations:0,certs:28},
    {state:'CA',status:'Compliant',lastCheck:'2026-04-12',violations:0,certs:19},
    {state:'NY',status:'Compliant',lastCheck:'2026-04-12',violations:0,certs:14},
  ];
  const auditLog = [
    {id:'A001',policy:'Commercial Auto — ABC Logistics',state:'GA',result:'Passed',cert:'AUD-2026-001',time:'2026-04-13 14:32'},
    {id:'A002',policy:'Whole Life — Rivera Family',state:'FL',result:'Review Required',cert:'—',time:'2026-04-13 13:15'},
    {id:'A003',policy:'Home — Johnson Family',state:'GA',result:'Passed',cert:'AUD-2026-002',time:'2026-04-13 11:08'},
    {id:'A004',policy:'GL — Southeast Catering',state:'GA',result:'Passed',cert:'AUD-2026-003',time:'2026-04-13 09:44'},
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Compliance Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">My Sentinel (Agent 11) — 50-State Monitoring Active</p>
        </div>
        <span className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />Sentinel Active
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[['Audit Shield™ Certs Issued','139','text-blue-600'],['Violations Stopped','4','text-red-600'],['States Monitored','5','text-purple-600'],['Compliance Rate','98.6%','text-green-600']].map(([l,v,c])=>(
          <div key={l as string} className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500">{l}</p>
            <p className={`text-2xl font-bold mt-1 ${c}`}>{v}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {(['overview','states','audit'] as const).map(t=>(
          <button key={t} onClick={()=>setActiveTab(t)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors capitalize ${activeTab===t?'bg-white shadow text-gray-900':'text-gray-600'}`}>{t==='audit'?'Audit Trail':t.charAt(0).toUpperCase()+t.slice(1)}</button>
        ))}
      </div>

      {activeTab==='overview' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">What My Sentinel Does For You</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[['Real-Time DOI Monitoring','Syncs with all state OCI feeds hourly for regulatory updates'],['Audit Shield™ Certificates','Cryptographically signed compliance proof for every validated policy'],['Bias Testing','Every AI decision tested for NAIC-compliant fairness standards'],['Instant Recall Alerts','Immediate notification when a regulation affects your existing book'],['50-State Coverage','All jurisdictions where you hold a license, monitored 24/7'],['Pre-Bind Validation','Every policy checked before Agent 12 is asked to bind']].map(([t,d])=>(
              <div key={t} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">✓</span>
                <div><p className="text-sm font-medium text-gray-900">{t}</p><p className="text-xs text-gray-500 mt-0.5">{d}</p></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab==='states' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>{['State','Status','Last Check','Violations','Certs Issued'].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {states.map(s=>(
                <tr key={s.state} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-bold text-gray-900">{s.state}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.status==='Compliant'?'bg-green-100 text-green-700':'bg-yellow-100 text-yellow-700'}`}>{s.status}</span></td>
                  <td className="px-4 py-3 text-gray-600">{s.lastCheck}</td>
                  <td className="px-4 py-3 text-gray-600">{s.violations}</td>
                  <td className="px-4 py-3 text-gray-600">{s.certs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab==='audit' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>{['Policy','State','Result','Cert ID','Time'].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {auditLog.map(a=>(
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{a.policy}</td>
                  <td className="px-4 py-3 text-gray-600">{a.state}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${a.result==='Passed'?'bg-green-100 text-green-700':'bg-yellow-100 text-yellow-700'}`}>{a.result}</span></td>
                  <td className="px-4 py-3 text-blue-600 font-mono text-xs">{a.cert}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{a.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
export default ComplianceDashboard;
